import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Beaker } from 'lucide-react';
import { dataMappingAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface AddMedicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMedicationModal: React.FC<AddMedicationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    med_id: '',
    data_sources_id: '',
    investigational_code: '',
    chemical_name: '',
    international_nonproprietary_name: '',
    generic_name: '',
    abbreviation1: '',
    abbreviation2: '',
    internal_name1: '',
    internal_name2: '',
    internal_name3: '',
    internal_name4: '',
    internal_name5: '',
    internal_name6: '',
    internal_name7: '',
    internal_name8: '',
    internal_name9: '',
    internal_name10: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftSaved, setDraftSaved] = useState(false);
  const draftKey = 'medication_mapping_draft_v1';

  useEffect(() => {
    if (isOpen) {
      // Restore draft
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          setFormData(prev => ({
            ...prev,
            ...parsed,
          }));
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [isOpen]);

  // Function to save draft
  const saveDraft = useCallback(() => {
    try {
      // Only save if there's actual content
      const hasContent = formData.med_id.trim() || 
                        formData.data_sources_id.trim() || 
                        formData.generic_name.trim() || 
                        formData.international_nonproprietary_name.trim() ||
                        formData.chemical_name.trim() ||
                        formData.investigational_code.trim();
      
      if (hasContent) {
        localStorage.setItem(draftKey, JSON.stringify(formData));
        setDraftSaved(true);
        // Hide the "draft saved" indicator after 2 seconds
        setTimeout(() => setDraftSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [formData, draftKey]);

  // Autosave draft when formData changes (with debouncing)
  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(saveDraft, 500); // Debounce for 500ms
    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, saveDraft]);

  // Save draft when window loses focus
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraft();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveDraft();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveDraft]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      await dataMappingAPI.createMedicationMapping(formData);
      toast.success('Medication mapping created successfully');
      
      // Clear form and draft
      setFormData({
        med_id: '',
        data_sources_id: '',
        investigational_code: '',
        chemical_name: '',
        international_nonproprietary_name: '',
        generic_name: '',
        abbreviation1: '',
        abbreviation2: '',
        internal_name1: '',
        internal_name2: '',
        internal_name3: '',
        internal_name4: '',
        internal_name5: '',
        internal_name6: '',
        internal_name7: '',
        internal_name8: '',
        internal_name9: '',
        internal_name10: '',
      });
      try { localStorage.removeItem(draftKey); } catch {}
      
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating medication mapping:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create medication mapping';
      toast.error(errorMessage);
      
      // Set field-specific errors if available
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Beaker className="h-6 w-6 text-[var(--accent)]" />
            <h2 className="text-xl font-semibold text-gray-900">Add Medication Mapping</h2>
            {draftSaved && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                Draft saved
              </span>
            )}
          </div>
          <button
            onClick={() => {
              onClose();
              // Clear draft when explicitly closing
              try { localStorage.removeItem(draftKey); } catch {}
            }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Med_ID and Data_Sources_ID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="med_id" className="block text-sm font-medium text-gray-700 mb-2">
                <Beaker className="inline h-4 w-4 mr-1" />
                Med_ID
              </label>
              <input
                type="text"
                id="med_id"
                value={formData.med_id}
                onChange={(e) => handleInputChange('med_id', e.target.value)}
                placeholder="M0001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                Unique identifier (M + 4 digits, e.g., M0001, M0002). Leave empty to auto-generate.
              </p>
            </div>
            <div>
              <label htmlFor="data_sources_id" className="block text-sm font-medium text-gray-700 mb-2">
                Data_Sources_ID
              </label>
              <input
                type="text"
                id="data_sources_id"
                value={formData.data_sources_id}
                onChange={(e) => handleInputChange('data_sources_id', e.target.value)}
                placeholder="D001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                Reference to Data Source Provider (e.g., D001, D002).
              </p>
            </div>
          </div>

          {/* Investigational Code and Chemical Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="investigational_code" className="block text-sm font-medium text-gray-700 mb-2">
                Investigational Code
              </label>
              <input
                type="text"
                id="investigational_code"
                value={formData.investigational_code}
                onChange={(e) => handleInputChange('investigational_code', e.target.value)}
                placeholder="INV-001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
            <div>
              <label htmlFor="chemical_name" className="block text-sm font-medium text-gray-700 mb-2">
                Chemical Name
              </label>
              <input
                type="text"
                id="chemical_name"
                value={formData.chemical_name}
                onChange={(e) => handleInputChange('chemical_name', e.target.value)}
                placeholder="Rifampicin"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
          </div>

          {/* Generic Name and INN */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="generic_name" className="block text-sm font-medium text-gray-700 mb-2">
                Generic Name *
              </label>
              <input
                type="text"
                id="generic_name"
                required
                value={formData.generic_name}
                onChange={(e) => handleInputChange('generic_name', e.target.value)}
                placeholder="Rifampin"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
            <div>
              <label htmlFor="international_nonproprietary_name" className="block text-sm font-medium text-gray-700 mb-2">
                International Nonproprietary Name (INN)
              </label>
              <input
                type="text"
                id="international_nonproprietary_name"
                value={formData.international_nonproprietary_name}
                onChange={(e) => handleInputChange('international_nonproprietary_name', e.target.value)}
                placeholder="Rifampicin"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
          </div>

          {/* Abbreviations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="abbreviation1" className="block text-sm font-medium text-gray-700 mb-2">
                Abbreviation 1
              </label>
              <input
                type="text"
                id="abbreviation1"
                value={formData.abbreviation1}
                onChange={(e) => handleInputChange('abbreviation1', e.target.value)}
                placeholder="RIF"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                3 letters (e.g., RIF, INH).
              </p>
            </div>
            <div>
              <label htmlFor="abbreviation2" className="block text-sm font-medium text-gray-700 mb-2">
                Abbreviation 2
              </label>
              <input
                type="text"
                id="abbreviation2"
                value={formData.abbreviation2}
                onChange={(e) => handleInputChange('abbreviation2', e.target.value)}
                placeholder="R"
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                1-2 letters (e.g., R, Pa).
              </p>
            </div>
          </div>

          {/* Internal Names (collapsible section) */}
          <details className="border border-gray-200 rounded-lg p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-4">
              Internal Names (Optional)
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <div key={num}>
                  <label htmlFor={`internal_name${num}`} className="block text-sm font-medium text-gray-700 mb-2">
                    Internal Name {num}
                  </label>
                  <input
                    type="text"
                    id={`internal_name${num}`}
                    value={formData[`internal_name${num}` as keyof typeof formData] as string}
                    onChange={(e) => handleInputChange(`internal_name${num}`, e.target.value)}
                    placeholder={`Internal name ${num}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
              ))}
            </div>
          </details>

          {/* Submit buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                onClose();
                // Clear draft when explicitly closing
                try { localStorage.removeItem(draftKey); } catch {}
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save Medication'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMedicationModal;
